// src/pages/PPStudents.jsx
import React, {
   useContext,
   useEffect,
   useMemo,
   useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import Footer from "../../components/Footer";
import ProfessorStudentsSection from "../../components/PPanel/ProfessorStudentsSection";

import { UserContext } from "../../UserContext";
import { fetchStudents } from "../../store/studentsSlice";

import {
   getMyGroupStudents,
   getMyGroupOverview,
} from "../../api/groupsService";

const STUDENTS_BATCH_SIZE = 16;

function extractMyGroups(res) {
   const groups =
      (Array.isArray(res?.groups) && res.groups) ||
      (Array.isArray(res?.data?.groups) && res.data.groups) ||
      (Array.isArray(res) && res) ||
      [];
   return groups.filter((g) => g && g.id != null);
}

function normalizeMyGroupsStudents(res) {
   // { totalGroups, totalStudents, students:[...] }
   const list = (Array.isArray(res?.students) && res.students) || [];
   return list.filter((s) => s && s.id != null);
}

/* ================= Students list ================= */
function CourseStudentsList({
   students,
   groupNameById,
   loadingAll,
   errorAll,
   myGroupStatus,
   batchSize = STUDENTS_BATCH_SIZE,
}) {
   return (
      <ProfessorStudentsSection
         students={students}
         groupNameById={groupNameById}
         loadingAll={loadingAll}
         errorAll={errorAll}
         status={myGroupStatus}
         batchSize={batchSize}
         title="Studentii mei"
         emptyHint="Nu esti asignat la nicio grupa sau nu ai studenti in grupe."
      />
   );
}

function PPStudents() {
   const { user } = useContext(UserContext);
   const dispatch = useDispatch();

   const studentsState = useSelector((state) => state.students || {});
   const studentsList = studentsState.list;
   const studentsLoading = !!studentsState.loading;
   const studentsError = studentsState.error || null;

   const usersById = useMemo(() => {
      const list = Array.isArray(studentsList) ? studentsList : [];
      const m = new Map();
      for (const u of list) m.set(String(u?.id), u);
      return m;
   }, [studentsList]);

   const [myGroupStatus, setMyGroupStatus] = useState("idle"); // idle | loading | ok | none | error
   const [myGroups, setMyGroups] = useState([]);
   const [apiStudents, setApiStudents] = useState([]);

   // 1) redux (opțional - îmbogățire doar)
   useEffect(() => {
      if (!user?.id) return;
      dispatch(fetchStudents());
   }, [dispatch, user?.id]);

   // 2) load overview + students (API)
   useEffect(() => {
      let cancelled = false;

      (async () => {
         if (!user?.id) return;

         if (user?.role !== "PROFESSOR") {
            setMyGroupStatus("error");
            return;
         }

         setMyGroupStatus("loading");

         try {
            const [studentsRes, overviewRes] = await Promise.all([
               getMyGroupStudents(),
               getMyGroupOverview(),
            ]);

            if (cancelled) return;

            const groups = extractMyGroups(overviewRes);
            const stList = normalizeMyGroupsStudents(studentsRes);

            setMyGroups(groups);
            setApiStudents(stList);

            // ✅ IMPORTANT: OK dacă ai ORI grupe ORI studenți
            if (groups.length === 0 && stList.length === 0)
               setMyGroupStatus("none");
            else setMyGroupStatus("ok");
         } catch {
            if (cancelled) return;

            setMyGroupStatus("error");
            setMyGroups([]);
            setApiStudents([]);
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [user?.id, user?.role]);

   // groupId -> name
   const groupNameById = useMemo(() => {
      const m = {};
      for (const g of myGroups) m[String(g.id)] = g?.name || `#${g.id}`;
      return m;
   }, [myGroups]);

   // ✅ lista afișată: API students (sigur), îmbogățit cu redux dacă există
   const mergedStudents = useMemo(() => {
      if (myGroupStatus !== "ok" && myGroupStatus !== "none") return [];

      return apiStudents
         .map((s) => {
            const u = usersById.get(String(s?.id));
            return {
               ...s,
               ...(u || {}),
               // păstrează groupId din API (important!)
               groupId: s?.groupId ?? u?.groupId,
            };
         })
         .filter((x) => x && x.id != null);
   }, [apiStudents, usersById, myGroupStatus]);

   const combinedError =
      myGroupStatus === "error"
         ? "Nu am putut încărca studenții/grupele profesorului."
         : studentsError
           ? String(studentsError)
           : null;

   return (
      <>
        

         <main className="main">
            <section className="professor single">
               {/* ✅ arată toate grupele primite */}
               {/*<MyGroupsSummary groups={myGroups} status={myGroupStatus} />*/}

               {/* ✅ arată toți studenții primiti */}
               <CourseStudentsList
                  students={mergedStudents}
                  groupNameById={groupNameById}
                  loadingAll={studentsLoading || myGroupStatus === "loading"}
                  errorAll={combinedError}
                  myGroupStatus={myGroupStatus}
                  batchSize={STUDENTS_BATCH_SIZE}
               />
            </section>

            <Footer />
         </main>
      </>
   );
}

export default PPStudents;
